// src/features/permissions/new.tsx

import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { InputField, SelectField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

// import options from the existing filter.tsx
import { levelOptions } from "./filter";

// Zod schema: `level` is required, `max_issuer_count` is optional
const FormSchema = z.object({
  level: z.coerce
    .number({ message: "Level is required" })
    .min(0, "Level is required"),
  max_issuer_count: z.coerce
    .number({ message: "Must be a number" })
    .optional()
    .nullable(),
});

export function PermissionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                Permissions {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row: level (select) & max_issuer_count (number) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="level"
                    label={t(`${table_name}.columns.level`, {
                      ns: "tables",
                      defaultValue: "Level",
                    })}
                    options={levelOptions}
                    control={form.control}
                    isRequired
                    allow_empty_option={false}
                  />
                  <InputField
                    name="max_issuer_count"
                    label={t(`${table_name}.columns.max_issuer_count`, {
                      ns: "tables",
                      defaultValue: "Max Issuer Count",
                    })}
                    type="number"
                    control={form.control}
                    allowZero
                    allowNegative={false}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (navigate back) */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
